import type { ReactElement } from "react";
import {
  Box,
  Typography,
  Link,
  Chip,
  Divider,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DrawerHeader from "./DrawerHeader";
import DrawerContentContainer from "./DrawerContentContainer";
import { StackedDrawerShell } from "./drawer-stack/StackedDrawerShell";
import type { StackedDrawerProps } from "./drawer-stack/types";

export interface TrackingHistoryEvent {
  updateDate?: string | null;
  status: string;
  location: string;
}

export interface TrackingDrawerProps extends StackedDrawerProps {
  trackingNumber: string;
  carrier: string;
  trackingUrl: string;
  shippedOn?: Date | null;
  deliveryOn?: Date | null;
  status?: string;
  statusChip?: ReactElement;
  salesOrder?: string;
  purchaseOrder?: string;
  weight?: string | null;
  receivedBy?: string | null;
  shipToAddress?: string[];
  shipToName?: string | null;
  shipToCompany?: string | null;
  trackingHistory?: TrackingHistoryEvent[];
}

/**
 * A stacked drawer that displays shipment tracking information.
 * Designed to be opened from within the Order Drawer when a user
 * clicks "Track Package."
 */
export function TrackingDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  trackingNumber,
  carrier,
  trackingUrl,
  shippedOn,
  deliveryOn,
  status,
  statusChip,
  salesOrder,
  purchaseOrder,
  weight,
  receivedBy,
  shipToAddress,
  shipToName,
  shipToCompany,
  trackingHistory,
}: TrackingDrawerProps) {
  const statusColor =
    status === "Delivered"
      ? "success"
      : status === "Shipped" || status === "Partially Shipped"
        ? "info"
        : "default";

  const groupedHistory = groupTrackingByDate(trackingHistory ?? []);

  return (
    <StackedDrawerShell
      open={open}
      onClose={onClose}
      onCloseAll={onCloseAll}
      zIndex={zIndex}
      depth={depth}
      showBackdrop
      ariaLabel={`Tracking details - ${trackingNumber}`}
      paperSx={{ width: { xs: "100%", sm: 560 } }}
    >
      <DrawerContentContainer sx={{ width: { sm: 560 } }}>
        <DrawerHeader
          iconElement={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LocalShippingIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="body1" fontWeight="normal">
                  Track Package
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {salesOrder ? `Order ${salesOrder}` : carrier}
                  {purchaseOrder ? ` PO # ${purchaseOrder}` : ""}
                </Typography>
              </Box>
            </Box>
          }
          onClose={onClose}
          customRightHeaderElement={
            statusChip ??
            (status ? (
              <Chip
                label={status}
                color={statusColor as "success" | "info" | "default"}
                size="small"
                variant="outlined"
              />
            ) : undefined)
          }
        />
        <Divider />

        <Box sx={{ flex: 1, overflow: "auto", bgcolor: "table.header" }}>
          {/* Shipment History Section */}
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 500 }}
            >
              Shipment history
            </Typography>

            {groupedHistory.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {groupedHistory.map((group, groupIdx) => (
                  <Box key={groupIdx} sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 0.5, display: "block", textAlign: "center" }}
                    >
                      {group.dateLabel}
                    </Typography>
                    {group.events.map((event, eventIdx) => {
                      const isLast =
                        groupIdx === groupedHistory.length - 1 &&
                        eventIdx === group.events.length - 1;
                      return (
                        <Box
                          key={eventIdx}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            py: 0.75,
                            pl: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 52, flexShrink: 0 }}
                          >
                            {event.time}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                                mt: 0.4,
                              }}
                            />
                            {!isLast && (
                              <Box
                                sx={{
                                  width: 2,
                                  flex: 1,
                                  minHeight: 16,
                                  bgcolor: "grey.300",
                                  mt: 0.25,
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ flex: 1 }}
                          >
                            {event.status}
                          </Typography>
                          {event.location && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: 12 }} />
                              {event.location}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No shipment history available yet.
              </Typography>
            )}
          </Box>

          {/* Shipment Information Section */}
          <Box sx={{ px: 3, pt: 2, pb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 500 }}
            >
              Shipment information
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Carrier & Tracking Number */}
              <Box sx={{ display: "flex", gap: 6 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Carrier
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <LocalShippingIcon sx={{ fontSize: 16 }} color="action" />
                    <Typography variant="body2">
                      {carrier} {deliveryOn ? "Delivery" : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tracking Number
                  </Typography>
                  <Box>
                    <Link
                      href={trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      {trackingNumber}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                </Box>
              </Box>

              {/* Weight */}
              {weight && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Weight
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {weight}
                  </Typography>
                </Box>
              )}

              {/* Shipped To */}
              {(shipToName || shipToAddress?.length) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Shipped To
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      mt: 0.5,
                    }}
                  >
                    {shipToName && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="body2">{shipToName}</Typography>
                      </Box>
                    )}
                    {shipToCompany && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="body2">
                          {shipToCompany}
                        </Typography>
                      </Box>
                    )}
                    {shipToAddress?.map((line, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {i === 0 && (
                          <LocationOnIcon sx={{ fontSize: 14 }} color="action" />
                        )}
                        {i > 0 && <Box sx={{ width: 14 }} />}
                        <Typography variant="body2">{line}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Proof of Delivery */}
              {receivedBy && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Proof of Delivery
                  </Typography>
                  <Typography variant="body2">
                    Package received by: {receivedBy}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DrawerContentContainer>
    </StackedDrawerShell>
  );
}

interface GroupedHistoryDay {
  dateLabel: string;
  events: { time: string; status: string; location: string }[];
}

function groupTrackingByDate(
  events: TrackingHistoryEvent[],
): GroupedHistoryDay[] {
  const sorted = [...events].sort((a, b) => {
    const da = a.updateDate ? new Date(a.updateDate).getTime() : 0;
    const db = b.updateDate ? new Date(b.updateDate).getTime() : 0;
    return db - da; // newest first
  });

  const groups = new Map<string, GroupedHistoryDay>();

  for (const event of sorted) {
    const date = event.updateDate ? new Date(event.updateDate) : null;
    const dateKey = date
      ? date.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "Unknown Date";
    const time = date
      ? date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "";

    if (!groups.has(dateKey)) {
      groups.set(dateKey, { dateLabel: dateKey, events: [] });
    }
    groups.get(dateKey)!.events.push({
      time,
      status: event.status,
      location: event.location,
    });
  }

  return Array.from(groups.values());
}
